/**
 * Internal and non-billable time, per person.
 *
 * WHY THIS PAGE EXISTS: the people board prices hours, so it drops anyone whose
 * billable value is zero -- and the people with the most non-billable time are
 * exactly those. Melissa Chavez logged 2,558.75 internal hours over the last
 * twelve closed months with no billable hours at all and appears nowhere else
 * in this product. Every person who booked time is on this board, and the
 * shares add up to one company.
 *
 * Four things the layout is defending, each of which changes what the page
 * means if it slips:
 *
 * 1. THE PERCENTAGE IS OF WORKED HOURS. Time off is out of the denominator, so
 *    PTO neither raises nor lowers it. Company-wide that is 56.7%; the same
 *    billable hours over TOTAL hours would read 50.1%, and somebody comparing
 *    the two will think one of them is wrong. The column help says which
 *    measure this is; nothing on the page computes the other one.
 * 2. TIME OFF IS NOT NON-BILLABLE TIME. `non_billable_hours` is internal plus
 *    unbillable client work and excludes time off entirely. The two sit in
 *    visually separated column groups because an hour of holiday is not an hour
 *    of unbilled effort, and a board that adds them makes a well-rested team
 *    look idle.
 * 3. HOURS ONLY. No rate, no dollar, nowhere -- TimeEntry.CostAmount is
 *    populated on about a third of rows, so anything summed from it
 *    under-reports by two thirds. `basis` says so and the page prints it, the
 *    way the technology board prints its own.
 * 4. THE TOTAL IS CHECKABLE. `reconciles_to` is the board plus
 *    `unattributed_hours` and equals the warehouse's own total exactly. The
 *    2.5 unattributed hours belong to no person; they are shown rather than
 *    dropped, because dropping them is what would make the total unverifiable.
 */
import { Clock } from "lucide-react";
import { useState } from "react";
import { PageHeaderBar } from "@/components/layout/PageHeaderBar";
import { usePageHeader } from "@/components/layout/PageHeaderContext";
import { InternalTimeDrillInDialog } from "@/components/profitability/InternalTimeDrillInDialog";
import { DataTable, ROW_CLASS, TOTAL_ROW_CLASS } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { InlineBar, type InlineBarSeries } from "@/components/shared/inline-bar";
import { KpiTile } from "@/components/shared/kpi-tile";
import { PanelTitle } from "@/components/shared/panel-title";
import { Card, CardHeader } from "@/components/ui/card";
import { useClientSort } from "@/hooks/use-client-sort";
import { useInternalTime } from "@/hooks/use-internal-time";
import { useRangeParam } from "@/hooks/use-range-param";
import { COLUMN_HELP } from "@/lib/column-help";
import { hours as formatHours, percent } from "@/lib/format";
import type { InternalTimeRow } from "@/lib/profitability-api";

const EMPTY_ROWS: InternalTimeRow[] = [];

/** Billable share (percent of WORKED hours) below which the bar reads bad / warn. */
const POPULATION_NOTE =
  "Where everybody's hours went, including the people the profitability boards cannot show, because none of their time was billable.";

const BILLABLE_BAD_BELOW = 60;
const BILLABLE_WARN_BELOW = 75;

function billableFill(pct: number): InlineBarSeries {
  if (pct < BILLABLE_BAD_BELOW) return "bad";
  if (pct < BILLABLE_WARN_BELOW) return "warn";
  return "series1";
}

/** The share as a bar beside its figure; null stays a dash, never a zero bar. */
function BillableCell({ pct }: { pct: number | null }) {
  if (pct === null) return <>{percent(pct)}</>;
  return (
    <InlineBar
      value={pct / 100}
      mode="plain"
      width={72}
      fill={billableFill(pct)}
      label={percent(pct)}
    />
  );
}

/**
 * The boundary between "work that earned nothing" and "not work".
 *
 * A rule down BOTH sides of the time-off column, and it is the only structural
 * line in the table. One side would only move the boundary -- Total would sit
 * inside the set-aside group and read as part of it. Colour or a heading would
 * not survive someone scanning a row of nine right-aligned numbers; a pair of
 * rules bracketing one column does.
 */
const TIME_OFF_EDGE = "border-x border-border";

export function InternalTimePage() {
  const [range, setRange] = useRangeParam();
  const { data, isLoading, isError, error, refetch } = useInternalTime(range);
  const [focusId, setFocusId] = useState<string | null>(null);

  const rows = data?.rows ?? EMPTY_ROWS;
  // In the browser, like the technology and Client Partner boards: a hundred
  // rows, all of them already here, and a round trip to reorder them would be
  // slower as well as more complicated. The default reproduces the service's
  // own order -- most internal hours first.
  const sorted = useClientSort(
    rows,
    (row, key) => row[key as keyof InternalTimeRow] as string | number | null,
    { key: "internal_hours", dir: "desc" },
  );
  const totals = data?.totals;
  const focused = rows.find((row) => row.person_id === focusId) ?? null;

  const rangeInfo = data?.range;
  // Published before the error branch, so a failed load still shows the title
  // and the range that produced it rather than an unlabelled error.
  usePageHeader(
    () => (
      <PageHeaderBar
        title="Internal time"
        fields={[
          totals
            ? {
                key: "population",
                label: "Population",
                content: `${totals.people} people who booked time`,
              }
            : null,
        ]}
        range={rangeInfo}
        rangeValue={range}
        onRangeChange={setRange}
      />
    ),
    [totals, rangeInfo, range, setRange],
  );

  if (isError) {
    return (
      <div className="w-full space-y-4">
        <ErrorState message={(error as Error)?.message} onRetry={() => refetch()} />
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiTile
          label="Billable share"
          value={percent(totals?.billable_pct)}
          help="Of WORKED hours. Time off is not in the denominator."
        />
        <KpiTile
          label="Worked hours"
          value={totals ? formatHours(totals.worked_hours) : "—"}
          hint={totals ? `${formatHours(totals.billable_hours)} h of it billable` : undefined}
        />
        <KpiTile
          label="Non-billable hours"
          value={totals ? formatHours(totals.non_billable_hours) : "—"}
          help="Internal plus unbillable client work. Time off is not in this."
        />
        {/* Its own card rather than a line under the one above it: the whole
            point is that these hours are a different kind of thing. */}
        <KpiTile
          label="Time off"
          value={totals ? formatHours(totals.time_off_hours) : "—"}
          help="Holiday, PTO and sick time. Not work, and not unbilled effort."
        />
      </div>

      <Card flush className="w-full">
        <CardHeader>
          {/* The population sentence (why this roster is larger than every
              profitability board's) and the long basis paragraph both ride on
              the title's (i). */}
          <PanelTitle help={[POPULATION_NOTE, data?.basis].filter((line) => line).join(" ")}>
            Hours by person
          </PanelTitle>
        </CardHeader>
        <DataTable
          flush
          dense
          loading={isLoading}
          sort={sorted.sort}
          sortDir={sorted.sortDir}
          onSort={sorted.toggleSort}
          headers={[
            { label: "Person", sortKey: "person", help: COLUMN_HELP.internalTimePerson },
            {
              label: "Billable %",
              sortKey: "billable_pct",
              className: "text-right",
              help: COLUMN_HELP.billablePct,
            },
            {
              label: "Worked",
              sortKey: "worked_hours",
              className: "text-right",
              help: COLUMN_HELP.workedHours,
            },
            {
              label: "Billable",
              sortKey: "billable_hours",
              className: "text-right",
              help: COLUMN_HELP.billableHours,
            },
            {
              label: "Internal",
              sortKey: "internal_hours",
              className: "text-right",
              help: COLUMN_HELP.internalHours,
            },
            {
              label: "Client, non-billable",
              sortKey: "client_non_billable_hours",
              className: "text-right",
              help: COLUMN_HELP.clientNonBillableHours,
            },
            {
              label: "Non-billable",
              sortKey: "non_billable_hours",
              className: "text-right",
              help: COLUMN_HELP.nonBillableTotalHours,
            },
            {
              label: "Time off",
              sortKey: "time_off_hours",
              className: `text-right ${TIME_OFF_EDGE}`,
              help: COLUMN_HELP.timeOffHours,
            },
            {
              label: "Total",
              sortKey: "total_hours",
              className: "text-right",
              help: COLUMN_HELP.internalTimeTotalHours,
            },
          ]}
        >
          {sorted.rows.map((row) => (
            <tr key={row.person_id} className={ROW_CLASS}>
              <td className="px-4 py-2.5 font-medium">
                <button
                  type="button"
                  className="text-left hover:underline"
                  onClick={() => setFocusId(row.person_id)}
                  title={`See where ${row.person}'s non-billable hours went`}
                >
                  {row.person}
                </button>
              </td>
              {/* `percent` renders null as a dash. Somebody who logged only time
                off has no billable share to state, and 0% would accuse them of
                a bad month they did not have. */}
              <td className="px-4 py-2.5 text-right tabular-nums">
                <BillableCell pct={row.billable_pct} />
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums">
                {formatHours(row.worked_hours)}
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums">
                {formatHours(row.billable_hours)}
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums">
                {formatHours(row.internal_hours)}
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums">
                {formatHours(row.client_non_billable_hours)}
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums">
                {formatHours(row.non_billable_hours)}
              </td>
              <td
                className={`px-4 py-2.5 text-right tabular-nums text-muted-foreground ${TIME_OFF_EDGE}`}
              >
                {formatHours(row.time_off_hours)}
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums">
                {formatHours(row.total_hours)}
              </td>
            </tr>
          ))}
          {totals && !isLoading ? (
            <tr className={TOTAL_ROW_CLASS}>
              <td className="px-4 py-2.5">Everyone ({totals.people})</td>
              <td className="px-4 py-2.5 text-right tabular-nums">
                <BillableCell pct={totals.billable_pct} />
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums">
                {formatHours(totals.worked_hours)}
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums">
                {formatHours(totals.billable_hours)}
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums">
                {formatHours(totals.internal_hours)}
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums">
                {formatHours(totals.client_non_billable_hours)}
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums">
                {formatHours(totals.non_billable_hours)}
              </td>
              <td className={`px-4 py-2.5 text-right tabular-nums ${TIME_OFF_EDGE}`}>
                {formatHours(totals.time_off_hours)}
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums">
                {formatHours(totals.total_hours)}
              </td>
            </tr>
          ) : null}
        </DataTable>
      </Card>

      {!isLoading && rows.length === 0 ? (
        <EmptyState
          icon={Clock}
          title="Nobody booked time in this range"
          description="No time entry falls inside the selected closed months."
        />
      ) : null}

      {totals && data ? <Reconciliation totals={totals} reconcilesTo={data.reconciles_to} /> : null}

      <InternalTimeDrillInDialog
        person={focused}
        range={range}
        onOpenChange={(open) => {
          if (!open) setFocusId(null);
        }}
      />
    </div>
  );
}

/**
 * The arithmetic that makes the board checkable, shown rather than asserted.
 *
 * `unattributed_hours` is 2.5 hours belonging to no person -- small enough to
 * round away and precisely the reason not to. It is the difference between a
 * total that agrees with the warehouse and one that is merely close to it, and
 * a page that quietly dropped it would be off by an amount nobody could name.
 *
 * `unclassified_hours` appears only when non-zero. It is zero live, and a
 * permanent "0.00 h unclassified" line is noise that trains people to skip the
 * paragraph the one interesting figure lives in.
 */
function Reconciliation({
  totals,
  reconcilesTo,
}: {
  totals: { total_hours: number; unattributed_hours: number; unclassified_hours: number };
  reconcilesTo: number;
}) {
  return (
    <p className="text-xs text-muted-foreground">
      {formatHours(totals.total_hours)} h across the people above, plus{" "}
      {/* Weighted only when there is something to weight. Emphasising "0.00 h"
          points the reader at a figure that is not telling them anything. */}
      <span className={totals.unattributed_hours > 0 ? "font-medium text-foreground" : undefined}>
        {formatHours(totals.unattributed_hours)} h
      </span>{" "}
      booked against no person, is {formatHours(reconcilesTo)} h &mdash; the warehouse's own total
      for these months, exactly.
      {totals.unclassified_hours > 0 ? (
        <>
          {" "}
          {formatHours(totals.unclassified_hours)} h carry a time-entry type none of the four
          buckets covers; they are inside the totals but in none of the columns between Billable and
          Time off.
        </>
      ) : null}
    </p>
  );
}
